import React, { useContext, useState, useRef } from 'react';
import classnames from 'classnames';
import _merge from 'lodash/merge';
import { getContext } from 'common';
import { ListContext } from 'components/List';
import { CONSTANTS } from 'common/ListKeyboardNav';

import {
  StepTitle,
  SubStepIndicator,
  StepLine,
  StepContext,
  HorizontalStepContext,
  StepFormContext,
} from 'components/StepForm';
import {
  getPressedColor,
  isSelectionKeyPressed,
} from './common/functions';

import DeprecatedRippleEffect from '../RippleEffect/DeprecatedRippleEffect';
import Styled from './styles';

const {
  LIST_ITEM_TABABLE_ITEM,
  LIST_ITEM_SELECTED,
} = CONSTANTS;

export interface SubStepProps {
  /** Override the default indicator used for the SubStep variant. Consider using `SubStepIndicator`. */
  indicator?: React.ReactNode;
  /** The title for the step. */
  title: string;
  /** The current selected step. Usually it is the variant=current step, unless it has error or warning. */
  selected?: boolean;
  /** The variant for styling purposes.  */
  variant?: 'current' | 'future' | 'error' | 'warning' | 'completed';
  /* If true, the step is disabled. Sub step is also disabled if the parent Step is disabled. */
  disabled?: boolean;
  /** Props to pass to the internal Step element. */
  internalStepProps?: Partial<React.ComponentPropsWithRef<'div'>>;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** If `true`, the button will be rendered without ripple effect. */
  disabledRipple?: boolean;
  /** Properties for the ripple style. */
  rippleStyle?: React.CSSProperties;
  /** @ignore */
  style?: React.CSSProperties;
  /** @ignore */
  onClick?: React.MouseEventHandler;
  /** @ignore */
  onKeyDown?: React.KeyboardEventHandler;
  /** @ignore */
  onKeyUp?: React.KeyboardEventHandler;
  /** @ignore */
  onMouseUp?: React.MouseEventHandler;
  /** @ignore */
  onMouseDown?: React.MouseEventHandler;
  /** @ignore */
  onMouseLeave?: React.MouseEventHandler;
  /** @ignore */
  onBlur?: React.FocusEventHandler;
}

/** `SubStep`, all other props will be provided to the root element */
const SubStep = React.forwardRef<HTMLElement, SubStepProps>((props: SubStepProps, ref) => getContext(({ RTL, theme }) => {
  const {
    indicator,
    title,
    selected,
    variant,
    disabled,
    theme: themeProp,
    disabledRipple,
    rippleStyle,
    internalStepProps,
    ...otherProps
  } = props;
  const { style: propsStyle, ...restOfOtherProps } = otherProps;
  const rippleRef = useRef(null);
  const subStepRef = useRef(null);
  const resolvedTheme = _merge({}, theme.STEP_FORM, themeProp.STEP_FORM || themeProp);
  const [isPressed, setIsPressed] = useState(false);
  const [pressedColor, setPressedColor] = useState(resolvedTheme.pressed.background);
  const listContext = useContext(ListContext);
  const stepFormContext = useContext(StepFormContext);
  const stepContext = useContext(StepContext);
  const horizontalStepContext = useContext(HorizontalStepContext);
  const resolvedDisabled = horizontalStepContext.disabled || stepContext.disabled || disabled;

  const handleOnKeyDown = (event) => {
    const { onKeyDown } = otherProps;
    if (isSelectionKeyPressed(event.key) && !resolvedDisabled) {
      setPressedColor(getPressedColor(resolvedTheme, variant, false));
      setIsPressed(true);
      rippleRef.current.handleKeyDown(event);
      subStepRef && subStepRef.current && subStepRef.current.focus();
    }
    onKeyDown && onKeyDown(event);
  };

  const handleOnKeyUp = (event) => {
    const { onKeyUp } = otherProps;
    !disabled && setIsPressed(false);
    onKeyUp && onKeyUp(event);
  };

  const handleClick = (event) => {
    const { onClick } = otherProps;
    event.preventDefault();
    if (!resolvedDisabled) {
      setPressedColor(getPressedColor(resolvedTheme, variant, true));
      subStepRef && subStepRef.current && subStepRef.current.focus();
    }
    onClick && onClick(event);
  };

  const handleMouseDown = (event) => {
    const { onMouseDown } = otherProps;
    !disabled && setIsPressed(true);
    onMouseDown && onMouseDown(event);
  };
  const handleMouseUp = (event) => {
    const { onMouseUp } = otherProps;
    !disabled && setIsPressed(false);
    onMouseUp && onMouseUp(event);
  };
  const handleMouseLeave = (event) => {
    const { onMouseLeave } = otherProps;
    !disabled && setIsPressed(false);
    onMouseLeave && onMouseLeave(event);
  };
  const handleBlur = (event) => {
    const { onBlur } = otherProps;
    !disabled && setIsPressed(false);
    onBlur && onBlur(event);
  };

  const classes = classnames({
    [`${LIST_ITEM_TABABLE_ITEM}-${listContext.uniqueListId}`]: !resolvedDisabled,
    [LIST_ITEM_SELECTED]: !resolvedDisabled && selected,
  });

  return (
    <Styled.StepWrapper
      as="div"
      ref={ref}
      rtl={RTL}
      $theme={resolvedTheme}
      $variant={variant}
      $isSubStep
      disabled={resolvedDisabled}
      $hoverEffect={stepFormContext.hoverEffect}
      aria-current={selected}
      {...restOfOtherProps}
      onKeyDown={handleOnKeyDown}
      onKeyUp={handleOnKeyUp}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onBlur={handleBlur}
    >
      <DeprecatedRippleEffect
        as="div"
        ref={rippleRef}
        disabled={disabledRipple || resolvedDisabled || stepContext.disableAnimation}
        style={{ height: '100%', width: '100%' }}
        rippleStyle={{ backgroundColor: pressedColor, ...rippleStyle }}
      >
        <Styled.SubStep
          as="li"
          className={classes}
          ref={subStepRef}
          rtl={RTL}
          tabIndex={-1}
          $theme={resolvedTheme.SUBSTEP}
          $variant={variant}
          $isLinear
          disabled={resolvedDisabled}
          {...internalStepProps}
        >
          <Styled.StepIndicatorContainer>
            <StepLine isSubStep $numberOfLines={0} />
            {indicator || <SubStepIndicator variant={variant} disabled={resolvedDisabled} theme={resolvedTheme} />}
            <StepLine isSubStep $numberOfLines={0} />
          </Styled.StepIndicatorContainer>
          <StepTitle isSubStep isPressed={isPressed} variant={variant} disabled={resolvedDisabled}>{title}</StepTitle>
        </Styled.SubStep>
      </DeprecatedRippleEffect>
    </Styled.StepWrapper>
  );
}));

SubStep.defaultProps = {
  indicator: undefined,
  selected: false,
  variant: 'future',
  disabled: false,
  theme: {},
  internalStepProps: {},
  disabledRipple: undefined,
  rippleStyle: {},
  style: {},
  onKeyDown: undefined,
  onClick: undefined,
  onKeyUp: undefined,
  onMouseDown: undefined,
  onMouseUp: undefined,
  onMouseLeave: undefined,
  onBlur: undefined,
};

SubStep.displayName = 'SubStep';

export { SubStep as default };
